import React from 'react';
import DateInputWithCalendar from '../DateInputWithCalendar';

// Extracted from StudentProfile.js's "student-details-slider" block (Phase
// 3 of the platform-health plan: split the file mechanically, without
// changing behavior). All state (studentData, AI recommendations,
// validation) and the field-change handlers stay in the parent — this is
// purely the collapsible details panel markup.
export default function ProfileDetailsForm({
  detailsOpen,
  onToggleDetails,
  onShowInviteLink,
  studentData,
  onFieldChange,
  onStatusChange,
  proficiencyLevels,
  statusOptions,
  validationErrors,
  aiLoading,
  aiError,
  aiRecommendations,
  onGenerateAIRecommendations,
}) {
  return (
    <div className="student-details-slider">
      <button className="details-toggle" onClick={onToggleDetails}>
        <span className={`chev${detailsOpen ? ' open' : ''}`}></span>
        <span className="details-title">Student details</span>
        <span className="details-sub">personal info, goals, contacts</span>
      </button>
      <div className={`details-panel${detailsOpen ? ' expanded' : ' collapsed'}`}>
      <div className="student-details-container">
        <div className="details-column">
          <div className="student-name-heading-row">
            <h3>Name</h3>
            {/* Same button/handler as the floating "Invite Link" FAB —
             * that FAB is hidden on mobile (see StudentProfile.css),
             * this is its mobile-only replacement, shown next to the
             * name field instead. Duplicated rather than physically
             * relocating the FAB via JS, so there's no window-width
             * check needed — each one is just a plain CSS media-query
             * show/hide of its own copy. */}
            <button
              type="button"
              className="student-name-invite-btn"
              title="Invite Link"
              onClick={onShowInviteLink}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Invite link
            </button>
          </div>
          <input
            type="text"
            name="name"
            data-tour="student-name"
            placeholder="Student Name"
            value={studentData.name || ''}
            onChange={onFieldChange}
            className="profile-input"
          />

          <h3>Proficiency Level</h3>
          <select
            name="proficiency"
            data-tour="student-proficiency"
            value={studentData.proficiency}
            onChange={onFieldChange}
            className="profile-select"
          >
            {proficiencyLevels.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>

          <h3>Date of Birth</h3>
          <div data-tour="student-dob">
            <DateInputWithCalendar
              name="dob"
              value={studentData.dob}
              onChange={onFieldChange}
            />
          </div>

          <h3>Price</h3>
          <div className="price-field-container" data-tour="student-price">
            <select
              name="priceCurrency"
              value={studentData.priceCurrency || '$'}
              onChange={onFieldChange}
              className="profile-select price-currency"
            >
              <option value="$">$</option>
              <option value="€">€</option>
              <option value="£">£</option>
              <option value="¥">¥</option>
              <option value="₽">₽</option>
              <option value="֏">֏</option>
              <option value="₾">₾</option>
              <option value="₸">₸</option>
              <option value="zł">zł</option>
              <option value="₴">₴</option>
            </select>
            <input
              type="number"
              name="priceAmount"
              value={studentData.priceAmount || ''}
              onChange={onFieldChange}
              className="profile-input price-amount"
              placeholder=""
              min="0"
              step="1"
              inputMode="numeric"
            />
            <span className="price-unit">per<br/>hour</span>
          </div>

          <h3>Status</h3>
          <select
            name="status"
            data-tour="student-status"
            value={studentData.status}
            onChange={onStatusChange}
            className="profile-select"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          {studentData.status === "on_vacation" && (
            <div className="conditional-dates">
              <label>Vacation From:</label>
              <DateInputWithCalendar
                name="vacationFrom"
                value={studentData.vacationFrom}
                onChange={onFieldChange}
              />
              <label>Vacation To:</label>
              <DateInputWithCalendar
                name="vacationTo"
                value={studentData.vacationTo}
                onChange={onFieldChange}
              />
            </div>
          )}

          {studentData.status === "sick_leave" && (
            <div className="conditional-dates">
              <label>Sick Leave From:</label>
              <DateInputWithCalendar
                name="sickLeaveFrom"
                value={studentData.sickLeaveFrom}
                onChange={onFieldChange}
              />
              <label>Sick Leave To:</label>
              <DateInputWithCalendar
                name="sickLeaveTo"
                value={studentData.sickLeaveTo}
                onChange={onFieldChange}
              />
            </div>
          )}

          {studentData.status === "paused" && (
            <div className="conditional-dates">
              <label>Resume Date:</label>
              <DateInputWithCalendar
                name="pausedResumeDate"
                value={studentData.pausedResumeDate}
                onChange={onFieldChange}
              />
            </div>
          )}
        </div>

        <div className="details-column" data-tour="student-profile-column">
          <h3>Experience</h3>
          <textarea
            name="experience"
            value={studentData.experience}
            onChange={onFieldChange}
            className="profile-textarea"
            rows="3"
            placeholder="Past learning background: years of English, schools or courses, exams taken (IELTS / TOEFL / Cambridge), time lived abroad, last teacher.&#10;e.g. Studied English 6 years at school; IELTS 5.5 in 2023; never lived abroad; took private lessons for 4 months."
          />

          <h3>Goals</h3>
          <textarea
            name="goals"
            value={studentData.goals}
            onChange={onFieldChange}
            className="profile-textarea"
            rows="3"
            placeholder="What the student wants to achieve — and by when. Be specific: exam target, work scenario, conversational fluency, writing skill.&#10;e.g. Pass IELTS 7.0 by June; lead client meetings in English; write reports without help."
          />

          <h3>Preferences</h3>
          <textarea
            name="preferences"
            value={studentData.preferences}
            onChange={onFieldChange}
            className="profile-textarea"
            rows="3"
            placeholder="How the student likes to learn: activity types they enjoy or avoid, pace, lesson length, homework load, video vs. text.&#10;e.g. Prefers conversation and role-play; dislikes long grammar drills; likes video clips; max 10 min of homework per day."
          />

          <h3>Hobbies</h3>
          <textarea
            name="hobbies"
            value={studentData.hobbies}
            onChange={onFieldChange}
            className="profile-textarea"
            rows="3"
            placeholder="Topics the student loves — used to personalise lesson material (articles, dialogues, examples).&#10;e.g. Football, travelling in Asia, sci-fi novels, cooking, board games, photography."
          />

          <h3>Strengths/Weaknesses</h3>
          <textarea
            name="strengthsWeaknesses"
            value={studentData.strengthsWeaknesses}
            onChange={onFieldChange}
            className="profile-textarea"
            rows="3"
            placeholder="What the student is strong at and what needs work. Mention skills (speaking, listening, writing, reading) and concrete grammar / vocab gaps.&#10;e.g. Strong: vocabulary, reading. Weak: present perfect, listening to fast speech, pronunciation of 'th'."
          />
        </div>

        <div className="details-column ai-recommendations-column" data-tour="student-ai-recs">
          <h3>
            <svg id="Master_Line" enableBackground="new 0 0 512 512" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
              <defs>
                <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#f093fb' }} />
                  <stop offset="100%" style={{ stopColor: '#f5576c' }} />
                </linearGradient>
              </defs>
              <g>
                <g>
                  <path d="m327.71 301.59c-126-19.98-130.85-170.94-130.84-194.41.01 23.47-4.85 174.43-130.84 194.41 126 19.98 130.85 170.94 130.84 194.41-.01-23.47 4.85-174.43 130.84-194.41z" fill="url(#iconGradient)"/>
                </g>
                <g>
                  <path d="m348.31 104.12c-57.11-9.05-59.31-77.48-59.31-88.12 0 10.64-2.2 79.07-59.31 88.12 57.11 9.06 59.31 77.49 59.31 88.12 0-10.63 2.2-79.06 59.31-88.12z" fill="url(#iconGradient)"/>
                </g>
                <g>
                  <path d="m445.97 233.86c-65.51-10.39-68.04-88.88-68.03-101.08.01 12.2-2.52 90.69-68.03 101.08 65.51 10.39 68.04 88.88 68.03 101.08-.01-12.2 2.52-90.69 68.03-101.08z" fill="url(#iconGradient)"/>
                </g>
              </g>
            </svg>
            AI Recommendations
          </h3>

          <button
            type="button"
            className="ai-generate-btn"
            onClick={onGenerateAIRecommendations}
            disabled={aiLoading}
            title="Generate personalised recommendations from the profile fields"
          >
            {aiLoading
              ? 'Generating…'
              : Object.keys(aiRecommendations).length === 0
                ? 'Generate AI recommendations'
                : 'Regenerate'}
          </button>

          {aiError && (
            <p className="ai-error">{aiError}</p>
          )}

          {Object.keys(aiRecommendations).length === 0 && !aiLoading && !aiError ? (
            <p className="no-recommendations">Fill in Experience / Goals / Preferences / Hobbies / Strengths-Weaknesses, then click <strong>Generate AI recommendations</strong>.</p>
          ) : (
            <div className="recommendations-list">
              {aiRecommendations.experience && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.experience }}></p>}
              {aiRecommendations.goals && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.goals }}></p>}
              {aiRecommendations.preferences && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.preferences }}></p>}
              {aiRecommendations.hobbies && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.hobbies }}></p>}
              {aiRecommendations.strengthsWeaknesses && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.strengthsWeaknesses }}></p>}
            </div>
          )}

        </div>

        <div className="details-column contact-info-column" data-tour="student-contact">
          <h3>Contact Info</h3>
          <div className="input-group">
            <span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 6L12 11L4 6H20Z" fill="currentColor"/>
              </svg>
            </span>
            <input
              type="email"
              name="contactEmail"
              value={studentData.isRegistered && studentData.userAccount
                ? studentData.userAccount.email
                : studentData.contactEmail}
              onChange={onFieldChange}
              className={`profile-input ${validationErrors.contactEmail ? 'input-error' : ''}`}
              placeholder="Email *"
              required
            />
          </div>
          <div className="input-group">
            <span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 19.58C15.77 19.94 16.29 20.06 16.77 19.83C18.25 19.26 19.92 18.8 21.6 18.4C22.06 18.29 22.33 17.78 22.21 17.3L19.46 12.35C19.24 11.96 18.79 11.83 18.42 11.97L15.78 12.98C15.45 13.11 15.08 13.02 14.85 12.79C14.05 11.99 12.97 10.91 12.17 10.11C11.94 9.88 11.85 9.51 11.98 9.18L12.99 6.54C13.13 6.17 13 5.72 12.65 5.5L7.7 2.75C7.22 2.53 6.71 2.8 6.62 3.26L6.62 10.79Z" fill="currentColor"/>
              </svg>
            </span>
            <input
              type="tel"
              name="contactPhone"
              value={
                studentData.isRegistered && studentData.userAccount && studentData.userAccount.phone
                  ? studentData.userAccount.phone
                  : studentData.contactPhone
              }
              onChange={onFieldChange}
              className="profile-input"
              placeholder="Phone"
            />
          </div>
        </div>
      </div>
        </div>
    </div>
  );
}
